import Swiper from 'swiper';
import { FreeMode, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import { RECT_WIPE_BOTTOM_CLOSED, RECT_WIPE_OPEN, OVERLAY_WIPE_OPEN_SECONDS, OVERLAY_WIPE_REDUCED_SECONDS, OVERLAY_WIPE_CLOSE_RATE } from './motion-tokens.js';


const showJournalDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
};

const closeJournalDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
};

const normalizeJournalGalleryUrl = (value = '') => {
    if (!value) return '';
    try {
        const url = new URL(value, window.location.href);
        const cleanPath = decodeURIComponent(url.pathname)
            .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
            .replace(/-scaled(?=\.[a-z0-9]+$)/i, '');
        return `${url.origin}${cleanPath}`.toLowerCase();
    } catch (error) {
        return String(value)
            .split(/[?#]/)[0]
            .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
            .replace(/-scaled(?=\.[a-z0-9]+$)/i, '')
            .toLowerCase();
    }
};

const createJournalWipeController = ({ dialog, gsap, lenis, reducedMotion = false }) => {
    const shell = dialog?.querySelector('[data-journal-modal-wipe]');
    if (!dialog || !shell || !gsap) return null;

    const duration = reducedMotion ? OVERLAY_WIPE_REDUCED_SECONDS : OVERLAY_WIPE_OPEN_SECONDS;
    let requestedOpen = false;

    // Keep the lightbox content fully rendered and let one rectangular mask
    // reveal the complete surface from the bottom edge upward.
    gsap.set(shell, {
        clipPath: RECT_WIPE_BOTTOM_CLOSED,
        force3D: true,
    });

    const resumePageScroll = () => {
        document.body.classList.remove('journal-modal-is-open');
        if (
            !document.body.classList.contains('menu-is-open')
            && !document.body.classList.contains('search-is-open')
            && !document.body.classList.contains('city-explorer-is-active')
            && !document.body.classList.contains('project-modal-is-open')
        ) {
            lenis?.start?.();
        }
    };

    const timeline = gsap.timeline({
        paused: true,
        defaults: { overwrite: 'auto' },
        onReverseComplete: () => {
            if (requestedOpen) return;
            closeJournalDialog(dialog);
            resumePageScroll();
        },
    });

    timeline.to(shell, {
        clipPath: RECT_WIPE_OPEN,
        duration,
        ease: reducedMotion ? 'none' : 'power4.inOut',
    });


    const open = () => {
        requestedOpen = true;

        // Commit the bottom-closed rectangle before the dialog is promoted to
        // the browser top layer, avoiding the one-frame full-screen flash.
        timeline.pause(0);
        gsap.set(shell, { clipPath: RECT_WIPE_BOTTOM_CLOSED, force3D: true });
        showJournalDialog(dialog);
        document.body.classList.add('journal-modal-is-open');
        lenis?.stop?.();
        shell.getBoundingClientRect();
        window.requestAnimationFrame(() => timeline.timeScale(1).play(0));
    };

    const close = () => {
        if (!dialog.open) return;
        requestedOpen = false;
        if (timeline.progress() <= 0.001) {
            closeJournalDialog(dialog);
            resumePageScroll();
            return;
        }
        timeline.timeScale(reducedMotion ? 2.5 : OVERLAY_WIPE_CLOSE_RATE).reverse();
    };

    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
    });

    return { open, close };
};

const initJournalContentGallery = ({ root, gsap, lenis, reducedMotion = false }) => {
    const dataNode = root.querySelector('[data-journal-gallery-data]');
    const lightbox = root.querySelector('[data-journal-lightbox]');
    if (!dataNode || !lightbox) return null;

    let items = [];
    try {
        const parsed = JSON.parse(dataNode.textContent || '[]');
        if (Array.isArray(parsed)) items = parsed.filter((item) => item && typeof item.url === 'string' && item.url.length > 0);
    } catch (error) {
        items = [];
    }
    if (!items.length) return null;

    const stage = lightbox.querySelector('[data-journal-lightbox-stage]');
    const image = lightbox.querySelector('[data-journal-lightbox-image]');
    const caption = lightbox.querySelector('[data-journal-lightbox-caption]');
    const counter = lightbox.querySelector('[data-journal-lightbox-counter]');
    const prev = lightbox.querySelector('[data-journal-lightbox-prev]');
    const next = lightbox.querySelector('[data-journal-lightbox-next]');
    const controller = createJournalWipeController({ dialog: lightbox, gsap, lenis, reducedMotion });
    let activeIndex = 0;
    let requestId = 0;

    const apply = (item, index) => {
        if (!image || !item) return;
        image.src = item.url;
        image.alt = item.alt || '';
        if (caption) {
            caption.textContent = item.caption || '';
            caption.hidden = !caption.textContent;
        }
        if (counter) counter.textContent = `${index + 1} / ${items.length}`;
    };

    const preloadAdjacent = () => {
        if (items.length < 2) return;
        [activeIndex - 1, activeIndex + 1].forEach((candidate) => {
            const item = items[(candidate + items.length) % items.length];
            if (!item?.url) return;
            const preload = new Image();
            preload.src = item.url;
        });
    };

    const update = (index, { animate = true, direction = 1 } = {}) => {
        if (!image || !items.length) return;
        const nextIndex = (index + items.length) % items.length;
        const item = items[nextIndex];
        activeIndex = nextIndex;
        const currentRequest = ++requestId;

        if (!animate || reducedMotion || !gsap) {
            apply(item, nextIndex);
            gsap?.set(image, { autoAlpha: 1, x: 0, xPercent: 0, scale: 1 });
            preloadAdjacent();
            return;
        }

        let committed = false;
        const commit = () => {
            if (committed || currentRequest !== requestId) return;
            committed = true;
            gsap.killTweensOf(image);
            gsap.to(image, {
                autoAlpha: 0,
                xPercent: -4 * direction,
                scale: 0.988,
                duration: 0.28,
                ease: 'power2.in',
                onComplete: () => {
                    if (currentRequest !== requestId) return;
                    apply(item, nextIndex);
                    gsap.fromTo(image,
                        { autoAlpha: 0, x: 0, xPercent: 4 * direction, scale: 0.988 },
                        { autoAlpha: 1, x: 0, xPercent: 0, scale: 1, duration: 0.72, ease: 'power3.out' },
                    );
                    preloadAdjacent();
                },
            });
        };

        const preload = new Image();
        preload.onload = commit;
        preload.onerror = commit;
        preload.src = item.url;
        if (preload.complete) commit();
    };

    const open = (index = 0) => {
        update(index, { animate: false });
        if (controller) controller.open();
        else {
            showJournalDialog(lightbox);
            lenis?.stop?.();
        }
    };

    const close = () => {
        if (controller) controller.close();
        else {
            closeJournalDialog(lightbox);
            lenis?.start?.();
        }
    };

    const indexesById = new Map();
    const indexesByUrl = new Map();
    items.forEach((item, index) => {
        const id = Number(item.id || 0);
        const url = normalizeJournalGalleryUrl(item.url);
        if (id > 0) {
            const indexes = indexesById.get(id) || [];
            indexes.push(index);
            indexesById.set(id, indexes);
        }
        if (url) {
            const indexes = indexesByUrl.get(url) || [];
            indexes.push(index);
            indexesByUrl.set(url, indexes);
        }
    });

    const occurrenceById = new Map();
    const occurrenceByUrl = new Map();
    root.querySelectorAll('[data-journal-story] img').forEach((contentImage) => {
        const classMatch = String(contentImage.className || '').match(/(?:^|\s)wp-image-(\d+)(?:\s|$)/);
        const attachmentId = classMatch ? Number(classMatch[1]) : 0;
        let index = -1;

        if (attachmentId > 0 && indexesById.has(attachmentId)) {
            const indexes = indexesById.get(attachmentId);
            const occurrence = occurrenceById.get(attachmentId) || 0;
            index = indexes[Math.min(occurrence, indexes.length - 1)] ?? -1;
            occurrenceById.set(attachmentId, occurrence + 1);
        }

        if (index < 0) {
            const candidates = [contentImage.currentSrc, contentImage.src, contentImage.getAttribute('src')]
                .filter(Boolean)
                .map(normalizeJournalGalleryUrl);
            for (const candidate of candidates) {
                const indexes = indexesByUrl.get(candidate) || [];
                if (!indexes.length) continue;
                const occurrence = occurrenceByUrl.get(candidate) || 0;
                index = indexes[Math.min(occurrence, indexes.length - 1)] ?? -1;
                occurrenceByUrl.set(candidate, occurrence + 1);
                break;
            }
        }

        if (index < 0) return;
        contentImage.dataset.journalGalleryImage = 'true';
        contentImage.dataset.journalGalleryIndex = String(index);
        contentImage.setAttribute('role', 'button');
        contentImage.setAttribute('tabindex', '0');
        contentImage.setAttribute('aria-label', root.dataset.journalGalleryOpenLabel || 'Open image in gallery');

        const activate = (event) => {
            event.preventDefault();
            event.stopPropagation();
            open(Number(contentImage.dataset.journalGalleryIndex || 0));
        };
        contentImage.addEventListener('click', activate);
        contentImage.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') activate(event);
        });
    });

    if (items.length <= 1) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
    }

    lightbox.querySelector('[data-journal-lightbox-close]')?.addEventListener('click', close);
    prev?.addEventListener('click', () => update(activeIndex - 1, { direction: -1 }));
    next?.addEventListener('click', () => update(activeIndex + 1, { direction: 1 }));
    lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') update(activeIndex - 1, { direction: -1 });
        if (event.key === 'ArrowRight') update(activeIndex + 1, { direction: 1 });
    });

    const settleDrag = () => {
        if (!image || !gsap) return;
        gsap.to(image, { x: 0, scale: 1, duration: 0.3, ease: 'power3.out', overwrite: true });
    };

    let swipe = null;
    stage?.addEventListener('pointerdown', (event) => {
        if (items.length <= 1 || (event.pointerType === 'mouse' && event.button !== 0)) return;
        swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dx: 0, horizontal: false };
        stage.setPointerCapture?.(event.pointerId);
        if (gsap && image) gsap.killTweensOf(image);
    });

    stage?.addEventListener('pointermove', (event) => {
        if (!swipe || event.pointerId !== swipe.id || !image) return;
        const dx = event.clientX - swipe.x;
        const dy = event.clientY - swipe.y;
        swipe.dx = dx;
        if (!swipe.horizontal && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(dy) * 1.05) swipe.horizontal = true;
        if (!swipe.horizontal) return;
        event.preventDefault();
        if (gsap) {
            const maxDrag = Math.min(window.innerWidth * 0.32, 180);
            gsap.set(image, { x: Math.max(-maxDrag, Math.min(maxDrag, dx * 0.68)), scale: 0.992 });
        }
    });

    const finishSwipe = (event) => {
        if (!swipe || event.pointerId !== swipe.id) return;
        const state = swipe;
        swipe = null;
        stage?.releasePointerCapture?.(event.pointerId);
        if (!state.horizontal) {
            settleDrag();
            return;
        }
        const elapsed = Math.max(16, performance.now() - state.time);
        const velocity = Math.abs(state.dx) / elapsed;
        const threshold = Math.min(44, Math.max(28, window.innerWidth * 0.07));
        if (Math.abs(state.dx) >= threshold || velocity > 0.32) {
            const direction = state.dx < 0 ? 1 : -1;
            if (gsap && image) gsap.set(image, { x: 0 });
            update(activeIndex + direction, { direction });
        } else {
            settleDrag();
        }
    };

    stage?.addEventListener('pointerup', finishSwipe);
    stage?.addEventListener('pointercancel', finishSwipe);

    return { close };
};

export function initSingleJournal({ lenis = null, gsap = null, ScrollTrigger = null, reducedMotion = false } = {}) {
    const root = document.querySelector('[data-single-journal]');
    if (!root) return;

    initJournalContentGallery({ root, gsap, lenis, reducedMotion });

    const links = [...root.querySelectorAll('[data-toc-link]')];
    const headingIds = [...new Set(links.map((link) => link.dataset.tocLink || '').filter(Boolean))];
    const headings = headingIds.map((id) => document.getElementById(id)).filter(Boolean);
    const feedbackNodes = [...root.querySelectorAll('[data-tool-feedback]')];
    let feedbackTimer = 0;

    const setFeedback = (message) => {
        feedbackNodes.forEach((node) => { node.textContent = message; });
        window.clearTimeout(feedbackTimer);
        feedbackTimer = window.setTimeout(() => {
            feedbackNodes.forEach((node) => { node.textContent = ''; });
        }, 2200);
    };

    const activate = (id) => {
        links.forEach((link) => {
            const active = link.dataset.tocLink === id;
            link.dataset.active = String(active);
            if (active) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
        });
    };

    links.forEach((link) => {
        link.addEventListener('click', (event) => {
            const id = link.dataset.tocLink;
            const target = id ? document.getElementById(id) : null;
            if (!target) return;

            event.preventDefault();
            activate(id);
            if (lenis?.scrollTo) {
                lenis.scrollTo(target, { offset: -40, duration: 1.08 });
            } else {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            window.history.replaceState(null, '', `#${id}`);
        });
    });

    let headingObserver = null;
    if (headings.length && 'IntersectionObserver' in window) {
        headingObserver = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]?.target?.id) activate(visible[0].target.id);
            },
            { rootMargin: '-15% 0px -67% 0px', threshold: [0, 1] },
        );
        headings.forEach((heading) => headingObserver.observe(heading));
    }

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setFeedback('Link copied');
        } catch (error) {
            const input = document.createElement('textarea');
            input.value = window.location.href;
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
            setFeedback('Link copied');
        }
    };

    root.querySelectorAll('[data-copy-url]').forEach((button) => button.addEventListener('click', copyUrl));
    root.querySelectorAll('[data-share-url]').forEach((button) => button.addEventListener('click', async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: document.title, url: window.location.href });
            } catch (error) {
                // Native share may be dismissed by the user.
            }
            return;
        }
        await copyUrl();
    }));
    root.querySelectorAll('[data-print-page]').forEach((button) => button.addEventListener('click', () => window.print()));

    const hero = root.querySelector('[data-journal-hero]');
    const heroImage = root.querySelector('[data-journal-hero-image]');
    const heroCopy = root.querySelector('[data-journal-hero-copy]');
    const story = root.querySelector('[data-journal-story]');
    const related = root.querySelector('[data-journal-related]');
    const dock = root.querySelector('[data-journal-dock]');
    const motionContext = gsap ? gsap.context(() => {
        if (!reducedMotion) {
            const introTargets = [heroCopy, hero].filter(Boolean);
            if (introTargets.length) {
                gsap.fromTo(introTargets, { autoAlpha: 0 }, {
                    autoAlpha: 1,
                    duration: 0.9,
                    stagger: 0.12,
                    ease: 'power2.out',
                });
            }

            if (hero && heroImage && ScrollTrigger) {
                gsap.set(heroImage, {
                    xPercent: 0,
                    yPercent: -1.5,
                    scale: 1,
                    transformOrigin: '50% 50%',
                });

                gsap.to(heroImage, {
                    yPercent: 4.5,
                    scale: 1.045,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: hero,
                        start: 'top 92%',
                        end: 'bottom 8%',
                        scrub: 1.35,
                        invalidateOnRefresh: true,
                    },
                });
            }

            if (story && ScrollTrigger) {
                [...story.children].forEach((block) => {
                    const media = block.matches('figure, .wp-block-gallery, .wp-block-cover, .wp-block-media-text, .wp-block-columns')
                        || Boolean(block.querySelector?.('figure, .wp-block-gallery, .wp-block-cover, .wp-block-media-text, .wp-block-columns'));
                    gsap.fromTo(block, { opacity: media ? 0.56 : 0.42 }, {
                        opacity: 1,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: block,
                            start: 'top 95%',
                            end: 'top 74%',
                            scrub: media ? 0.52 : 0.42,
                        },
                    });
                });
            }

            const relatedHeader = related?.querySelector('[data-journal-related-header]');
            const relatedCards = related ? [...related.querySelectorAll('[data-journal-related-card]')] : [];
            if (relatedHeader && ScrollTrigger) {
                gsap.fromTo([...relatedHeader.children], { autoAlpha: 0, y: 20 }, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.88,
                    stagger: 0.09,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: relatedHeader, start: 'top 90%', once: true },
                });
            }
            if (relatedCards.length && ScrollTrigger) {
                gsap.fromTo(relatedCards, { autoAlpha: 0.36, y: 24 }, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.95,
                    stagger: 0.07,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: related.querySelector('[data-journal-related-slider]') || related,
                        start: 'top 88%',
                        once: true,
                    },
                });
            }
        }
    }, root) : null;

    const topicsToggle = root.querySelector('[data-journal-topics-toggle]');
    const topicsPanel = root.querySelector('[data-journal-topics-panel]');
    const topicsClose = root.querySelector('[data-journal-topics-close]');
    const topicsChevron = root.querySelector('[data-journal-topics-chevron]');
    let topicsOpen = false;

    const setTopicsOpen = (open, { immediate = false } = {}) => {
        if (!topicsToggle || !topicsPanel) return;
        topicsOpen = Boolean(open);
        topicsToggle.setAttribute('aria-expanded', String(topicsOpen));
        topicsPanel.setAttribute('aria-hidden', String(!topicsOpen));
        topicsPanel.style.pointerEvents = topicsOpen ? 'auto' : 'none';

        if (topicsChevron) topicsChevron.style.transform = topicsOpen ? 'rotate(180deg)' : 'rotate(0deg)';

        if (!gsap || reducedMotion || immediate) {
            topicsPanel.style.opacity = topicsOpen ? '1' : '0';
            topicsPanel.style.visibility = topicsOpen ? 'visible' : 'hidden';
            return;
        }

        gsap.to(topicsPanel, {
            autoAlpha: topicsOpen ? 1 : 0,
            y: topicsOpen ? 0 : 12,
            duration: topicsOpen ? 0.42 : 0.28,
            ease: topicsOpen ? 'power3.out' : 'power2.inOut',
            overwrite: true,
        });
    };

    topicsToggle?.addEventListener('click', (event) => {
        event.stopPropagation();
        setTopicsOpen(!topicsOpen);
    });
    topicsClose?.addEventListener('click', () => setTopicsOpen(false));
    topicsPanel?.addEventListener('click', (event) => event.stopPropagation());
    topicsPanel?.querySelectorAll('[data-toc-link]').forEach((link) => {
        link.addEventListener('click', () => setTopicsOpen(false));
    });

    const handleTopicsOutside = (event) => {
        if (!topicsOpen) return;
        if (topicsPanel?.contains(event.target) || topicsToggle?.contains(event.target)) return;
        setTopicsOpen(false);
    };
    const handleTopicsEscape = (event) => {
        if (event.key === 'Escape' && topicsOpen) {
            setTopicsOpen(false);
            topicsToggle?.focus();
        }
    };
    document.addEventListener('pointerdown', handleTopicsOutside);
    document.addEventListener('keydown', handleTopicsEscape);
    setTopicsOpen(false, { immediate: true });

    let dockTrigger = null;
    if (dock && hero && ScrollTrigger) {
        const showDock = () => {
            dock.style.pointerEvents = 'auto';
            if (!gsap || reducedMotion) {
                dock.style.opacity = '1';
                dock.style.transform = 'none';
                return;
            }
            gsap.to(dock, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power3.out', overwrite: true });
        };
        const hideDock = () => {
            setTopicsOpen(false);
            dock.style.pointerEvents = 'none';
            if (!gsap || reducedMotion) {
                dock.style.opacity = '0';
                return;
            }
            gsap.to(dock, { autoAlpha: 0, y: 16, duration: 0.28, ease: 'power2.out', overwrite: true });
        };

        if (gsap && !reducedMotion) gsap.set(dock, { autoAlpha: 0, y: 16 });
        else hideDock();

        dockTrigger = ScrollTrigger.create({
            trigger: hero,
            start: 'bottom 88%',
            endTrigger: related || root,
            end: related ? 'top bottom' : 'bottom bottom',
            onEnter: showDock,
            onEnterBack: showDock,
            onLeave: hideDock,
            onLeaveBack: hideDock,
            onRefresh: (self) => (self.isActive ? showDock() : hideDock()),
        });
    }

    const slider = root.querySelector('[data-journal-related-slider]');
    let relatedSwiper = null;
    if (slider) {
        const progress = slider.querySelector('[data-journal-related-progress]');
        relatedSwiper = new Swiper(slider, {
            modules: [FreeMode, Navigation],
            slidesPerView: 1.08,
            spaceBetween: 18,
            speed: 1080,
            grabCursor: true,
            resistanceRatio: 0.72,
            watchOverflow: true,
            freeMode: {
                enabled: true,
                momentum: true,
                momentumRatio: 0.72,
                momentumVelocityRatio: 0.72,
                minimumVelocity: 0.015,
                sticky: false,
            },
            breakpoints: {
                768: { slidesPerView: 1.45, spaceBetween: 24 },
                1024: { slidesPerView: 2, spaceBetween: 32, freeMode: { enabled: false } },
            },
            navigation: {
                prevEl: slider.querySelector('.journal-related__prev'),
                nextEl: slider.querySelector('.journal-related__next'),
            },
            on: {
                init(swiper) {
                    if (progress) progress.style.width = `${Math.max(0, Math.min(1, swiper.progress || 0)) * 100}%`;
                },
                progress(swiper, value) {
                    if (progress) progress.style.width = `${Math.max(0, Math.min(1, value)) * 100}%`;
                },
            },
        });
    }

    window.addEventListener('pagehide', () => {
        headingObserver?.disconnect();
        document.removeEventListener('pointerdown', handleTopicsOutside);
        document.removeEventListener('keydown', handleTopicsEscape);
        dockTrigger?.kill();
        motionContext?.revert?.();
        relatedSwiper?.destroy?.(true, true);
    }, { once: true });
}
